import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/api";
import type { SystemUser } from "../../../types/system-user";
import { BackButton } from "../../../components/button/back-button";
import { useToast } from "../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../utils";
import type { UserSystemRoles } from "../../../types/enum/user-system-roles.enum";
import { useAuth } from "../../../hooks/use-auth";
import { LoadingButton } from "../../../components/button/loading-button";
import { APP_RESOURCES, AccessControl } from "../../../permissions/access-control";

type CreateSystemUserDto = {
  name: string;
  email: string;
  password: string;
  role: UserSystemRoles;
};

export default function CadastroSystemUser() {
  const { error, success } = useToast();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const canAssignAdminRole = AccessControl.canView(
    APP_RESOURCES.CONFIG_SYSTEM_USER_ASSIGN_ADMIN,
    authUser?.role,
  );

  const { id } = useParams<{ id: string }>();

  const [user, setUser] = useState<CreateSystemUserDto>({
    name: "",
    email: "",
    password: "",
    role: "ATTENDANT",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      try {
        api.get<SystemUser>(`/system-user/${id}`).then((res) => {
          setUser(res.data);
        });
      } catch (err) {
        const apiMessage = getApiErrorMessage(err);
        error(apiMessage);
      }
    }
  }, [id, error]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      if (id) {
        setIsSubmitting(true);
        await api.patch<Partial<CreateSystemUserDto>>(
          `/system-user/${id}`,
          user,
        );
        success("Tenant atualizado com sucesso!");
      } else {
        if (user.password.length < 8) {
          error("A senha deve ter pelo menos 8 caracteres");
          return;
        }

        if (user.password !== confirmPassword) {
          error("As senhas não coincidem");
          return;
        }

        setIsSubmitting(true);
        await api.post<CreateSystemUserDto>("/system-user", user);
        success("Usuário do sistema cadastrado com sucesso!");
      }

      navigate("/painel/config/system-user");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-page">
      <BackButton />
      <h1 className="app-page-title mb-6">Novo Usuário do Sistema</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="form-label">Nome</label>
          <input
            type="text"
            name="name"
            value={user.name || ""}
            onChange={handleChange}
            className="form-input"
            required
          />
        </div>

        {/* E-mail */}
        <div>
          <label className="form-label">E-mail</label>
          <input
            type="email"
            name="email"
            value={user.email || ""}
            onChange={handleChange}
            className="form-input"
            required
          />
        </div>

        {!id && (
          <>
            {/* Senha */}
            <div>
              <label className="form-label">Senha</label>
              <input
                type="password"
                name="password"
                value={user.password || ""}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            {/* Confirmar Senha */}
            <div>
              <label className="form-label">Confirmar Senha</label>
              <input
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input"
                required
              />
            </div>
          </>
        )}

        {/* Função */}
        <div>
          <label className="form-label">Função</label>
          <select
            name="role"
            value={user.role}
            onChange={handleChange}
            className="form-input"
            required
          >
            {canAssignAdminRole && (
              <option value="ADMIN">Administrador Geral</option>
            )}
            <option value="ATTENDANT">Atendente</option>
            <option value="TENANT_ADMIN">Administrador do Ambiente</option>
          </select>
        </div>

        {/* Botão */}
        <LoadingButton
          type="submit"
          className="btn btn-neutral cursor-pointer"
          isLoading={isSubmitting}
          loadingLabel={id ? "Salvando" : "Cadastrando"}
        >
          {id ? "Salvar Alterações" : "Cadastrar Usuário"}
        </LoadingButton>
      </form>
    </div>
  );
}
